import { useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  FormControl,
  InputLabel,
  MenuItem,
  Modal,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Typography,
  IconButton
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import UnfoldMoreIcon from "@mui/icons-material/UnfoldMore";
import DeleteIcon from "@mui/icons-material/Delete";
import { supabase, getAuthHeaders } from "../../supabase";
import { stickyHeaderCellStyle, cellStyle, modalStyle } from "../../assets/dashboardStyles";
import RenameUserModal from "./RenameUserModal";

export default function UsersTablePage({ user }) {
  const [users, setUsers] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(20);
  const [sortConfig, setSortConfig] = useState({ key: "created_at", direction: "desc" });
  const [openModal, setOpenModal] = useState(false);
  const [renameModalOpen, setRenameModalOpen] = useState(false);
  const columns = ["name", "email", "role", "created_at"];
  const [loading, setLoading] = useState(false);

  const [newUser, setNewUser] = useState({
    email: "",
    name: "",
    role: "staff",
  });

  const handleDeleteUser = async (id) => {
    const confirmed = window.confirm("Are you sure you want to delete this user?");
    if (!confirmed) return;
  
    const { error } = await supabase.from("user").delete().eq("id", id);
    if (error) {
      alert("Failed to delete user: " + error.message);
    } else {
      setUsers((prev) => prev.filter((u) => u.id !== id));
      alert("User deleted successfully.");
    }
  };

  const fetchUsers = async () => {
    const { data, error } = await supabase.from("user").select(`id, name, db_name, ${columns.join(",")}`);
    if (error) console.error("Error fetching users:", error.message);
    else setUsers(data);
  };

  useEffect(() => {
    fetchUsers();
  }, []);

  const handleSort = (column) => {
    setSortConfig((prev) =>
      prev.key === column
        ? { key: column, direction: prev.direction === "asc" ? "desc" : "asc" }
        : { key: column, direction: "asc" }
    );
  };

  const sortedUsers = useMemo(() => {
    return [...users].sort((a, b) => {
      const aVal = a[sortConfig.key] || "";
      const bVal = b[sortConfig.key] || "";
      return sortConfig.direction === "asc"
        ? String(aVal).localeCompare(String(bVal))
        : String(bVal).localeCompare(String(aVal));
    });
  }, [users, sortConfig]);

  const filteredUsers = useMemo(() => {
    return sortedUsers.filter((user) =>
      Object.values(user).some((value) =>
        String(value || "").toLowerCase().includes(searchQuery.toLowerCase())
      )
    );
  }, [sortedUsers, searchQuery]);

  const paginatedUsers = useMemo(() => {
    return filteredUsers.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);
  }, [filteredUsers, page, rowsPerPage]);

  const handleCreateUser = async () => {
    if (!newUser.email) return alert("Please enter an email.");
    setLoading(true);

    const charset = "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789!@#$%";
    const bytes = crypto.getRandomValues(new Uint8Array(16));
    const generatedPassword = Array.from(bytes)
      .map((b) => charset[b % charset.length])
      .join("");

    const { error } = await supabase.functions.invoke('create-user', {
      method: "POST",
      body: {
        email: newUser.email,
        password: generatedPassword,
        user_meta: newUser
      },
    });

    if (error) {
      alert("Error creating user: " + error.message);
      return;
    }

    // Send welcome email with login credentials
    let emailSent = true;
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL || "";
      const headers = await getAuthHeaders();
      const resp = await fetch(`${backendUrl}/api/user/send-welcome-email`, {
        method: "POST",
        headers,
        body: JSON.stringify({ email: newUser.email, password: generatedPassword }),
      });
      if (!resp.ok) {
        const payload = await resp.json().catch(() => ({}));
        throw new Error(payload.error || `Email failed (${resp.status})`);
      }
    } catch (emailErr) {
      emailSent = false;
      console.error("Failed to send welcome email:", emailErr);
      alert(`User created, but the welcome email failed. Please resend manually.\n\nLogin: ${newUser.email}\nPassword: ${generatedPassword}`);
    }

    if (emailSent) {
      alert("User created successfully.");
    }
    setLoading(false);
    setOpenModal(false);
    fetchUsers();
  };

  return (
    <Box sx={{ width: "100%", mx: "auto", mt: 7, px: { xs: 1, md: 3 } }}>
        {/* SEARCH + ADD USER HEADER */}
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 2, mb: 2, width: 1  }}>
          <Box sx={{ display: "flex", alignItems: "center", bgcolor: "#EBEBEB", borderRadius: 2, px: 2, py: 0.5, maxWidth: 375 }}>
            <SearchIcon sx={{ color: "grey.600" }} />
            <TextField
              placeholder="Search any column"
              variant="standard"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              fullWidth
              InputProps={{
                disableUnderline: true,
                sx: {
                  ml: 1,
                  color: "black",
                  "& input": { color: "black" },
                },
              }}
            />
          </Box>

          <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
            <Button 
              variant="outlined" 
              onClick={() => setOpenModal(true)} 
              disableElevation
            >
              Add User
            </Button>
            {user?.role === "admin" && (
              <Button 
                variant="outlined" 
                onClick={() => setRenameModalOpen(true)} 
                disableElevation
              >
                Rename User
              </Button>
            )}
          </Box>
        </Box>

        {/* TABLE */}
        <TableContainer
          elevation={0}
          component={Paper} 
          sx={{ 
            bgcolor: "#EBEBEB", 
            borderRadius: 2, 
            maxHeight: "65vh", 
            width: { xs: '100vw', md: 1 },
            ml: { xs: -1, md: 0 } 
        }}>
          <Table stickyHeader size="small">
            <TableHead>
              <TableRow>
                {columns.map((col) => (
                    <TableCell
                      key={col}
                      sx={{
                        ...stickyHeaderCellStyle,
                        fontWeight: "normal", 
                        textTransform: "none",
                        fontSize: 14,          
                        cursor: "pointer",
                      }}
                      onClick={() => handleSort(col)}
                    >
                      {col.replaceAll("_", " ")}
                      <UnfoldMoreIcon sx={{ fontSize: 14, ml: 0.5, verticalAlign: "middle" }} />
                    </TableCell>
                  )
                )}
              <TableCell sx={stickyHeaderCellStyle}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {paginatedUsers.map((user) => (
                <TableRow key={user.id}>
                  {columns.map((col) => (
                      <TableCell key={col} sx={cellStyle}>
                        {col === "created_at"
                          ? new Date(user[col]).toLocaleString()
                          : user[col] || "N/A"}
                      </TableCell>
                    )
                  )}
                  <TableCell sx={cellStyle}>
                    <IconButton
                      onClick={() => handleDeleteUser(user.id)}
                      aria-label="delete"
                      sx={{ color: "#d32f2f" }}
                    >
                      <DeleteIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>

        <TablePagination
          rowsPerPageOptions={[20, 100]}
          component="div"
          count={filteredUsers.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={(e, p) => setPage(p)}
          onRowsPerPageChange={(e) => {
            setRowsPerPage(parseInt(e.target.value, 10));
            setPage(0);
          }}
        />
        <Modal open={openModal} onClose={() => setOpenModal(false)}>
          <Box
            sx={{
              ...modalStyle,
              backgroundColor: "#ffffff",
              color: "#111",
              borderRadius: 2,
              p: 4,
            }}
          >
            <Typography id="modal-title" variant="h6" sx={{ textAlign: "center", mb: 3, fontSize: 18 }}>
              Create a New User
            </Typography>

            <TextField
              fullWidth
              label="Email Address"
              value={newUser.email}
              onChange={(e) => setNewUser({ ...newUser, email: e.target.value })}
              variant="outlined"
              sx={{ mb: 3 }}
            />

            <TextField
              fullWidth
              label="Name"
              value={newUser.name}
              onChange={(e) => setNewUser({ ...newUser, name: e.target.value })}
              variant="outlined"
              sx={{ mb: 2 }}
            />

            <TextField
              fullWidth
              label="Database Name (Short)"
              value={newUser.db_name}
              onChange={(e) => setNewUser({ ...newUser, db_name: e.target.value })}
              variant="outlined"
              sx={{ mb: 2 }}
            />

            <FormControl fullWidth sx={{ mb: 2 }}>
              <InputLabel>Staff Type</InputLabel>
              <Select
                value={newUser.role}
                onChange={(e) => setNewUser({ ...newUser, role: e.target.value })}
                label="Staff Type"
              >
                <MenuItem value="staff">Staff</MenuItem>
                <MenuItem value="admin">Admin</MenuItem>
                <MenuItem value="guest">Guest</MenuItem>
              </Select>
            </FormControl>

            <Button
              fullWidth
              variant="contained"
              onClick={handleCreateUser}
              disabled={loading}
            >
              Create User
            </Button>

            <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 2 }}>
              <Button
                variant="text"
                sx={{
                  color: "#333",
                  fontWeight: 600,
                  textTransform: "none",
                  "&:hover": {
                    textDecoration: "underline",
                    background: "transparent",
                  },
                }}
                onClick={() => setOpenModal(false)}
              >
                Cancel
              </Button>
            </Box>
          </Box>
        </Modal>
        
        <RenameUserModal
          open={renameModalOpen}
          onClose={() => setRenameModalOpen(false)}
          users={users}
          onSuccess={fetchUsers}
        />
    </Box>
  );
}
